/**
 * BPAFindingDetailModal
 *
 * Modal for viewing the full details of a single BPA finding.
 * Fetches detail data via the service layer rather than calling
 * `callApi` directly.
 */

import CustomModalWrapper from 'components/CustomModalWrapper/CustomModalWrapper';
import DetailTable from 'components/DetailTable';
import { ConfigItem, StructuredDataObject } from 'components/DetailTable/types';
import LoadingScreen from 'components/LoadingScreen';
import ModalDialog from 'components/ModalDialog';
import { useAtomValue } from 'jotai';
import { extractApiErrorMessage } from 'lib/helper';
import moment from 'moment';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Button, ButtonGroup, Col, Row } from 'reactstrap';
import { companyIdAtom } from 'views/state';

import BPAFindingAgeHistoryTab from './BPAFindingAgeHistoryTab';
import BPAFindingPermissionsTab from './BPAFindingPermissionsTab';
import BPAFindingRemediationTab from './BPAFindingRemediationTab';
import BPAFindingStatusHistoryTab from './BPAFindingStatusHistoryTab';
import BPAFindingSummaryCard, {
  BPAFindingSummaryData,
} from './BPAFindingSummaryCard';
import { useBPAFindingAgeHistory } from '../../hooks/useBPAFindingAgeHistory';
import {
  fetchBPAFindingDetail,
  fetchBPAFindingStatusHistory,
  refreshBPAFindingPermissions,
} from '../../services/bpaFindingsService';
import {
  BPAFindingPermissionsStatus,
  RecordStatusHistoryItem,
} from '../../types/bpaFindings.types';
import './BPAFindingDetailModal.css';

interface Props {
  findingId: number;
  onClose: () => void;
  isOpen?: boolean;
}

type FindingPermissions = Record<string, unknown>;

interface BPAFindingDetailResponse extends BPAFindingSummaryData {
  t12_finding_item_id?: string | null;
  resource_link?: string | null;
  first_seen_at?: string | null;
  last_seen_at?: string | null;
  last_fixed_at?: string | null;
  last_status_change_at?: string | null;
  age?: number | null;
  last_user_status_changed_at?: string | null;
  last_user_status_changed_by?: string | null;
  last_user_status_reason?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  scan_status_display?: string | null;
  integration?: {
    id?: number | null;
    t12_integration_id?: string | null;
    integration_label?: string | null;
    integration_type?: string | null;
    integration_type_display?: string | null;
    account_id?: string | null;
    provider_code?: string | null;
    module_code?: string | null;
  } | null;
  benchmark?: {
    id?: number | null;
    name?: string | null;
    description?: string | null;
  } | null;
  control?: {
    id?: number | string | null;
    control_id?: string | null;
    description?: string | null;
    severity?: string | null;
    severity_number?: number | null;
    service?: string | null;
    sub_control_id?: string | null;
    sub_description?: string | null;
    remediation_instructions?: StructuredDataObject | null;
  } | null;
  last_scan?: Record<string, unknown> | null;
  permissions?: FindingPermissions | null;
  permissions_status: BPAFindingPermissionsStatus;
  additional_info?: Record<string, unknown> | null;
  ignored_details?: string;
}

type FindingDetailTabId =
  | 'finding-summary'
  | 'remediation'
  | 'general-information'
  | 'scan-details'
  | 'status-history'
  | 'age-history'
  | 'permissions';

const escapeHtml = (value?: string | null): string =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatIgnoredDetails = (
  finding: BPAFindingDetailResponse | null,
): string => {
  if (!finding || finding.user_status !== 'Ignored') return '';

  const ignoredParts: string[] = [];
  if (
    finding.last_user_status_changed_at &&
    moment(finding.last_user_status_changed_at).isValid()
  ) {
    ignoredParts.push(
      `Ignored at ${moment(finding.last_user_status_changed_at).format(
        'MMM D, YYYY h:mm A',
      )}`,
    );
  }
  if (finding.last_user_status_changed_by) {
    ignoredParts.push(`by ${escapeHtml(finding.last_user_status_changed_by)}`);
  }

  const detailText =
    ignoredParts.length > 0 ? `Yes (${ignoredParts.join(', ')})` : 'Yes';
  const commentText = finding.last_user_status_reason
    ? `<br />Comments: ${escapeHtml(finding.last_user_status_reason)}`
    : '';

  return `${detailText}${commentText}`;
};

const BPAFindingDetailModal: React.FC<Props> = ({
  findingId,
  onClose,
  isOpen: _isOpen = true,
}) => {
  const companyId = useAtomValue(companyIdAtom);
  const [finding, setFinding] = useState<BPAFindingDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] =
    useState<FindingDetailTabId>('finding-summary');
  const ageHistory = useBPAFindingAgeHistory({
    companyId,
    enabled: activeTab === 'age-history',
    findingId,
  });
  const [historyItems, setHistoryItems] = useState<RecordStatusHistoryItem[]>(
    [],
  );
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const historyRequestRef = useRef(0);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionsError, setPermissionsError] = useState('');
  const permissionsRequestRef = useRef(0);
  const permissionsLoadingRef = useRef(false);

  useEffect(() => {
    setActiveTab('finding-summary');
  }, [companyId, findingId]);

  useEffect(() => {
    permissionsRequestRef.current += 1;
    permissionsLoadingRef.current = false;
    setPermissionsLoading(false);
    setPermissionsError('');

    return () => {
      permissionsRequestRef.current += 1;
    };
  }, [companyId, findingId]);

  useEffect(() => {
    if (!findingId) {
      setLoading(false);
      return;
    }
    if (!companyId || companyId === -1) {
      setFinding(null);
      setLoading(false);
      setError('Please select a company before viewing finding details.');
      return;
    }

    let cancelled = false;

    const loadDetail = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetchBPAFindingDetail(findingId, companyId);
        if (!cancelled) {
          setFinding(response.data as BPAFindingDetailResponse);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(
            extractApiErrorMessage(err, 'Failed to fetch finding details.'),
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadDetail();

    return () => {
      cancelled = true;
    };
  }, [companyId, findingId]);

  const loadPermissions = useCallback(async () => {
    if (
      permissionsLoadingRef.current ||
      !findingId ||
      !companyId ||
      companyId === -1
    ) {
      return;
    }

    const requestId = permissionsRequestRef.current + 1;
    permissionsRequestRef.current = requestId;
    permissionsLoadingRef.current = true;
    setPermissionsLoading(true);
    setPermissionsError('');

    try {
      const response = await refreshBPAFindingPermissions(findingId, companyId);
      if (permissionsRequestRef.current === requestId) {
        setFinding((current) =>
          current?.id === findingId
            ? {
                ...current,
                permissions: response.permissions,
                permissions_status: response.permissions_status,
              }
            : current,
        );
      }
    } catch (err: unknown) {
      if (permissionsRequestRef.current === requestId) {
        setPermissionsError(
          extractApiErrorMessage(
            err,
            'Failed to fetch finding permission details.',
          ),
        );
      }
    } finally {
      if (permissionsRequestRef.current === requestId) {
        permissionsLoadingRef.current = false;
        setPermissionsLoading(false);
      }
    }
  }, [companyId, findingId]);

  useEffect(() => {
    if (
      finding?.id === findingId &&
      finding.permissions_status === 'refresh_required'
    ) {
      void loadPermissions();
    }
  }, [finding?.id, finding?.permissions_status, findingId, loadPermissions]);

  useEffect(() => {
    historyRequestRef.current += 1;
    setHistoryItems([]);
    setHistoryLoading(false);
    setHistoryLoaded(false);
    setHistoryError('');
  }, [companyId, findingId]);

  useEffect(() => {
    if (
      activeTab !== 'status-history' ||
      historyLoaded ||
      historyLoading ||
      !findingId
    ) {
      return;
    }

    if (!companyId || companyId === -1) {
      setHistoryError('Please select a company before viewing status history.');
      return;
    }

    const requestId = historyRequestRef.current + 1;
    historyRequestRef.current = requestId;

    const loadStatusHistory = async () => {
      setHistoryLoading(true);
      setHistoryError('');
      try {
        const history = await fetchBPAFindingStatusHistory(
          findingId,
          companyId,
        );
        if (historyRequestRef.current === requestId) {
          setHistoryItems(history);
          setHistoryLoaded(true);
        }
      } catch (err: unknown) {
        if (historyRequestRef.current === requestId) {
          setHistoryError(
            extractApiErrorMessage(
              err,
              'Failed to fetch finding status history.',
            ),
          );
          setHistoryLoaded(true);
        }
      } finally {
        if (historyRequestRef.current === requestId) {
          setHistoryLoading(false);
        }
      }
    };

    void loadStatusHistory();
  }, [activeTab, companyId, findingId, historyLoaded, historyLoading]);

  const isIgnored = finding?.user_status === 'Ignored';
  const permissionsApplicable =
    finding?.permissions_status === 'ready' ||
    finding?.permissions_status === 'refresh_required';
  const detailData = finding
    ? {
        ...finding,
        age_display: finding.age == null ? null : finding.age.toFixed(1),
        ignored_details: formatIgnoredDetails(finding),
      }
    : finding;

  const findingSummaryConfig: ConfigItem[] = [
    { name: 'Finding ID', key: 't12_finding_item_id', type: 'string' },
    { name: 'Description', key: 'description', type: 'string' },
    { name: 'Status', key: 'status_display', type: 'string' },
    { name: 'Age (Days)', key: 'age_display', type: 'string' },
    ...(isIgnored
      ? [
          {
            name: 'Ignored',
            key: 'ignored_details',
            type: 'html' as const,
          },
        ]
      : []),
    { name: 'Resource Link', key: 'resource_link', type: 'url' },
    { name: 'First Detected Date', key: 'first_seen_at', type: 'date' },
    { name: 'Last Detected Date', key: 'last_seen_at', type: 'date' },
    { name: 'Last Fixed Date', key: 'last_fixed_at', type: 'date' },
  ];

  const lastScanConfig: ConfigItem[] = [
    { name: 'Status', key: 'last_scan.status', type: 'string' },
    { name: 'Benchmark', key: 'last_scan.benchmark', type: 'string' },
    { name: 'Scan Type', key: 'last_scan.job_type', type: 'string' },
    { name: 'Scan Mode', key: 'last_scan.trigger_type', type: 'string' },
    { name: 'Region', key: 'last_scan.region', type: 'string' },
    { name: 'Started Date', key: 'last_scan.started_at', type: 'date' },
    { name: 'Finished Date', key: 'last_scan.finished_at', type: 'date' },
    { name: 'Duration', key: 'last_scan.scan_duration', type: 'string' },
  ];

  const controlDetails = finding?.control || {};

  const generalInfoConfig: ConfigItem[] = [
    { name: 'Finding ID', key: 't12_finding_item_id', type: 'string' },
    {
      name: 'Integration Details',
      key: 'integration',
      type: 'object',
      config: [
        { name: 'Account ID', key: 'account_id', type: 'string' },
        { name: 'Provider', key: 'provider_code', type: 'string' },
        { name: 'Module', key: 'module_code', type: 'string' },
        { name: 'Type', key: 'integration_type_display', type: 'string' },
      ],
    },
    {
      name: 'Benchmark',
      key: 'benchmark',
      type: 'object',
      config: [{ name: 'Name', key: 'description', type: 'string' }],
    },
    {
      name: 'Control Details',
      key: 'control',
      type: 'object',
      config: [
        { name: 'Control ID', key: 'id', type: 'string' },
        { name: 'Description', key: 'description', type: 'string' },
        { name: 'Severity', key: 'severity', type: 'string' },
        { name: 'Service', key: 'service', type: 'string' },
        ...(controlDetails.sub_control_id
          ? [
              {
                name: 'Sub Control ID',
                key: 'sub_control_id',
                type: 'string' as const,
              },
            ]
          : []),
        ...(controlDetails.sub_description
          ? [
              {
                name: 'Sub Description',
                key: 'sub_description',
                type: 'string' as const,
              },
            ]
          : []),
      ],
    },
    { name: 'Created At', key: 'created_at', type: 'date' },
    { name: 'Updated At', key: 'updated_at', type: 'date' },
  ];

  const tabs: Array<{
    id: FindingDetailTabId;
    title: string;
    config?: ConfigItem[];
    data?: BPAFindingDetailResponse | null;
    render?: () => React.ReactNode;
  }> = [
    {
      id: 'finding-summary',
      title: 'Finding Summary',
      config: findingSummaryConfig,
      data: detailData,
    },
    {
      id: 'remediation',
      title: 'Remediation',
      render: () => (
        <BPAFindingRemediationTab
          remediation={finding?.control?.remediation_instructions}
        />
      ),
    },
    {
      id: 'general-information',
      title: 'General Information',
      config: generalInfoConfig,
      data: detailData,
    },
    {
      id: 'scan-details',
      title: 'Scan Details',
      config: lastScanConfig,
      data: detailData,
    },
    {
      id: 'status-history',
      title: 'Status History',
      render: () => (
        <BPAFindingStatusHistoryTab
          error={historyError}
          isLoading={historyLoading}
          items={historyItems}
        />
      ),
    },
    {
      id: 'age-history',
      title: 'Age History',
      render: () => (
        <BPAFindingAgeHistoryTab
          error={ageHistory.error}
          isLoading={ageHistory.isLoading}
          items={ageHistory.items}
          onRetry={() => void ageHistory.retry()}
        />
      ),
    },
    ...(permissionsApplicable
      ? [
          {
            id: 'permissions' as const,
            title: 'Permissions',
            render: () => (
              <BPAFindingPermissionsTab
                error={permissionsError}
                isLoading={permissionsLoading}
                onRetry={() => void loadPermissions()}
                permissions={finding?.permissions}
                service={finding?.control?.service}
                status={finding?.permissions_status || 'not_applicable'}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <ModalDialog
      title="Finding Details"
      size="lg"
      className="bpa-finding-detail-dialog"
      onClose={onClose}
      closeOnEscape
      childComponent={
        <>
          {loading && <LoadingScreen />}
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
          {!loading && !error && finding && (
            <CustomModalWrapper id="bpa-finding-scroll">
              <Fragment>
                <Row className="bpa-finding-detail-layout">
                  <Col
                    sm="6"
                    md="4"
                    lg="3"
                    className="bpa-finding-detail-nav-col"
                  >
                    <ButtonGroup
                      vertical
                      className="btn-group-toggle"
                      style={{ maxWidth: '100%', width: '100%' }}
                    >
                      {tabs.map((tab) => (
                        <Button
                          key={tab.id}
                          type="button"
                          style={{
                            padding: '10px',
                            margin: '0px',
                            textAlign: 'left',
                          }}
                          color={activeTab === tab.id ? 'primary' : 'secondary'}
                          size="sm"
                          title={tab.title}
                          onClick={() => setActiveTab(tab.id)}
                        >
                          {tab.title}
                        </Button>
                      ))}
                    </ButtonGroup>
                  </Col>
                  <Col
                    sm="6"
                    md="8"
                    lg="9"
                    className="bpa-finding-detail-content-col"
                  >
                    <CustomModalWrapper id="scroll-wrapper">
                      {tabs.map(
                        (tab) =>
                          activeTab === tab.id && (
                            <Fragment key={tab.id}>
                              <Row>
                                <Col sm="12">
                                  <BPAFindingSummaryCard rowData={finding} />
                                </Col>
                              </Row>
                              {tab.render ? (
                                tab.render()
                              ) : (
                                <DetailTable
                                  data={tab.data}
                                  config={tab.config as ConfigItem[]}
                                />
                              )}
                            </Fragment>
                          ),
                      )}
                    </CustomModalWrapper>
                  </Col>
                </Row>
              </Fragment>
            </CustomModalWrapper>
          )}
        </>
      }
    />
  );
};

export default BPAFindingDetailModal;
